import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import RequiredMark from '@/temu-platform/components/RequiredMark';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { buildTemuTemplateChildPath } from '@/temu-platform/constants/temuRoutes';
import { loadProductMetaBySkc } from '@/temu-platform/utils/temuProductMetaBySkc';
import { styleNoFromSupplierCode } from '@/temu-platform/utils/temuStyleNo';
import { showToast } from '@/utils/toast';

const COMBO_MENU_GAP = 4;
const COMBO_MENU_MAX_HEIGHT = 220;
const COMBO_VIEWPORT_PADDING = 8;

function parseTypeOptions(res) {
  const list = res?.certificateTypeInfoList
    || res?.data?.certificateTypeInfoList
    || res?.info?.certificateTypeInfoList
    || [];
  return Array.isArray(list) ? list.filter((item) => Number(item?.certificateTypeId) !== 844) : [];
}

function splitSkcNames(value) {
  if (!value) return [];
  return String(value)
    .split(/[,\s，]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function resolveProductId(seed, skcName) {
  const raw = Number(seed?.productId || 0);
  if (Number.isFinite(raw) && raw > 0) return raw;
  const skcs = splitSkcNames(skcName);
  if (skcs.length === 1 && /^\d+$/.test(skcs[0])) return Number(skcs[0]);
  return 0;
}

/** itemType：2 或名称含图/实拍 → 走 flash.real.image；其余文件走 uploadFile */
function isImageNeedItem(item) {
  const type = Number(item?.itemType);
  if (type === 2) return true;
  const name = String(item?.itemName || item?.name || '').toLowerCase();
  return /图|实拍|photo|image|pic|label/.test(name);
}

function parseNeedUploadItems(res) {
  const list = res?.result?.needUploadItems
    || res?.needUploadItems
    || res?.result?.items
    || [];
  return Array.isArray(list) ? list : [];
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const idx = text.indexOf(',');
      resolve(idx >= 0 ? text.slice(idx + 1) : text);
    };
    reader.onerror = () => reject(new Error(i18n.t('certForm.readFailed', { ns: 'workbench' })));
    reader.readAsDataURL(file);
  });
}

function certDisplayName(row) {
  const files = Array.isArray(row?.fileList) ? row.fileList : [];
  const fromFiles = files.map((f) => f?.fileName).filter(Boolean);
  return row?.name
    || row?.fileName
    || fromFiles[0]
    || row?.title
    || row?.poolSn
    || i18n.t('certForm.unnamed', { ns: 'workbench' });
}

/**
 * 可输入筛选的证书名称下拉；菜单挂载到 body，避免被弹窗 overflow 裁切。
 */
function CertificateNameCombobox({
  options,
  value,
  onChange,
  disabled = false,
  loading = false,
  placeholder = '',
}) {
  const { t } = useTranslation('workbench');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);

  const selected = useMemo(
    () => options.find((item) => String(item.poolSn) === String(value)) || null,
    [options, value],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((item) => {
      const hay = `${item.label || ''} ${item.poolSn || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [options, query]);

  const updateMenuPosition = useCallback(() => {
    const trigger = inputRef.current;
    const menu = menuRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menu?.offsetHeight || COMBO_MENU_MAX_HEIGHT;
    const spaceBelow = window.innerHeight - rect.bottom - COMBO_VIEWPORT_PADDING;
    const spaceAbove = rect.top - COMBO_VIEWPORT_PADDING;
    const openUp = spaceBelow < Math.min(menuHeight, COMBO_MENU_MAX_HEIGHT) && spaceAbove > spaceBelow;

    let top;
    if (openUp) {
      top = Math.max(COMBO_VIEWPORT_PADDING, rect.top - COMBO_MENU_GAP - menuHeight);
    } else {
      top = Math.min(
        window.innerHeight - COMBO_VIEWPORT_PADDING - Math.min(menuHeight, COMBO_MENU_MAX_HEIGHT),
        rect.bottom + COMBO_MENU_GAP,
      );
    }

    setMenuStyle({
      top: `${top}px`,
      left: `${rect.left}px`,
      width: `${rect.width}px`,
    });
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery(selected?.label || '');
    }
  }, [open, selected]);

  useLayoutEffect(() => {
    if (!open || disabled) {
      setMenuStyle(null);
      return undefined;
    }
    updateMenuPosition();
    const frame = requestAnimationFrame(updateMenuPosition);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, disabled, filtered.length, loading, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      const target = e.target;
      if (rootRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const menu = open && !disabled ? (
    <div
      ref={menuRef}
      className="tpo-cert-name-combo__menu tpo-cert-name-combo__menu--portal"
      role="listbox"
      style={menuStyle || { visibility: 'hidden' }}
    >
      {loading ? (
        <div className="tpo-cert-name-combo__empty">{t('returnList.loading')}</div>
      ) : filtered.length === 0 ? (
        <div className="tpo-cert-name-combo__empty">{t('certForm.noMatchCert')}</div>
      ) : (
        filtered.map((item) => (
          <button
            key={item.poolSn}
            type="button"
            role="option"
            className={`tpo-cert-name-combo__option${String(item.poolSn) === String(value) ? ' is-active' : ''}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              onChange?.(item.poolSn);
              setQuery(item.label);
              setOpen(false);
            }}
          >
            <span className="tpo-cert-name-combo__name">{item.label}</span>
            <span className="tpo-muted tpo-cert-name-combo__sn">{item.poolSn}</span>
          </button>
        ))
      )}
    </div>
  ) : null;

  return (
    <div className={`tpo-cert-name-combo${disabled ? ' is-disabled' : ''}`} ref={rootRef}>
      <input
        ref={inputRef}
        className="tpo-input"
        type="text"
        disabled={disabled}
        value={open ? query : (selected?.label || query || '')}
        placeholder={loading ? t('certForm.loadingCerts') : (placeholder || t('certForm.filterPh'))}
        onFocus={() => {
          if (disabled) return;
          setOpen(true);
          setQuery(selected?.label || '');
        }}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (value) onChange?.('');
        }}
        autoComplete="off"
      />
      {menu && typeof document !== 'undefined' ? createPortal(menu, document.body) : null}
    </div>
  );
}

/**
 * 产品合规 · 证书绑定：按商品货号推导款号，从证书库带出同款号证书并绑定到 SKC。
 */
export default function CertificateSavePanel({
  open = false,
  shopId,
  seed = null,
  onSaved,
  onClose,
}) {
  const { t } = useTranslation('workbench');
  const [skcName, setSkcName] = useState('');
  const [certificateTypeId, setCertificateTypeId] = useState('');
  const [poolSn, setPoolSn] = useState('');
  const [styleKey, setStyleKey] = useState('');
  const [styleNoLabel, setStyleNoLabel] = useState('');
  const [typeOptions, setTypeOptions] = useState([]);
  const [certOptions, setCertOptions] = useState([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [needItems, setNeedItems] = useState([]);
  const [needLoading, setNeedLoading] = useState(false);
  const [needUploads, setNeedUploads] = useState({});
  const autoPickedRef = useRef(false);

  const libraryPath = buildTemuTemplateChildPath('certificates');
  const productId = resolveProductId(seed, skcName);

  useEffect(() => {
    if (!open) return;
    const fromNames = splitSkcNames(seed?.skcNames);
    const fromName = splitSkcNames(seed?.skcName);
    const skcs = fromNames.length ? fromNames : fromName;
    setSkcName(skcs.join(','));
    setCertificateTypeId(seed?.certificateTypeId != null ? String(seed.certificateTypeId) : '');
    setPoolSn(seed?.poolSn || '');
    setStyleKey('');
    setStyleNoLabel('');
    setCertOptions([]);
    setNeedItems([]);
    setNeedUploads({});
    autoPickedRef.current = Boolean(seed?.poolSn);
  }, [open, seed]);

  // 编辑/上传弹窗：按商品 + 证书类型拉 queryNeedUploadItems，决定表单要展示哪些填写/上传项
  useEffect(() => {
    const pid = resolveProductId(seed, skcName);
    if (!open || !shopId || !(pid > 0) || !certificateTypeId) {
      setNeedItems([]);
      return undefined;
    }
    const certType = Number(certificateTypeId);
    if (!(certType > 0)) {
      setNeedItems([]);
      return undefined;
    }
    let cancelled = false;
    setNeedLoading(true);
    temuPlatformAPI.queryCertificateNeedUpload(shopId, { productId: pid, certType })
      .then((res) => {
        if (cancelled) return;
        setNeedItems(parseNeedUploadItems(res));
        setNeedUploads({});
      })
      .catch((e) => {
        if (!cancelled) {
          setNeedItems([]);
          showToast(e?.message || t('certForm.queryNeedFailed'), 'warning');
        }
      })
      .finally(() => {
        if (!cancelled) setNeedLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, seed, skcName, certificateTypeId, t]);

  useEffect(() => {
    if (!open || !shopId) return;
    temuPlatformAPI.listCertificateTypes(shopId)
      .then((res) => setTypeOptions(parseTypeOptions(res)))
      .catch(() => setTypeOptions([]));
  }, [open, shopId]);

  // 按 SKC 货号推导款号，用于匹配证书库
  useEffect(() => {
    if (!open || !shopId) {
      setStyleKey('');
      setStyleNoLabel('');
      return undefined;
    }
    const skcs = splitSkcNames(skcName);
    if (!skcs.length) {
      setStyleKey('');
      setStyleNoLabel('');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const metaMap = await loadProductMetaBySkc(shopId, skcs.slice(0, 20));
        if (cancelled) return;
        let supplierCode = '';
        for (const skc of skcs) {
          const code = String(metaMap?.[skc]?.supplierCode || '').trim();
          if (code) {
            supplierCode = code;
            break;
          }
        }
        const styleNo = String(styleNoFromSupplierCode(supplierCode) || '').trim();
        setStyleNoLabel(styleNo);
        setStyleKey(styleNo.toLowerCase());
      } catch {
        if (!cancelled) {
          setStyleKey('');
          setStyleNoLabel('');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, skcName]);

  useEffect(() => {
    if (!open || !shopId || !certificateTypeId) {
      setCertOptions([]);
      return undefined;
    }
    let cancelled = false;
    setCertsLoading(true);
    // 优先拉有款号的可绑定模板；失败再退回按类型的平台列表
    Promise.all([
      temuPlatformAPI.listCertificateLibrary(shopId, {
        source: 'style-bind',
        page: 1,
        pageSize: 200,
      }).catch(() => null),
      temuPlatformAPI.listCertificateLibrary(shopId, {
        source: 'platform',
        page: 1,
        pageSize: 100,
        certificateTypeId,
      }).catch(() => null),
    ])
      .then(([styleRes, typeRes]) => {
        if (cancelled) return;
        const styleList = Array.isArray(styleRes?.list) ? styleRes.list : [];
        const typeList = Array.isArray(typeRes?.list) ? typeRes.list : [];
        const typeId = String(certificateTypeId);
        const byPool = new Map();
        const pushRow = (row, preferStyle = false) => {
          const sn = String(row?.poolSn || '').trim();
          if (!sn) return;
          const rowType = String(row?.certificateTypeId ?? '').trim();
          if (rowType && rowType !== typeId) return;
          const styleNo = String(row?.styleNo || '').trim();
          const prev = byPool.get(sn);
          if (prev && !preferStyle) return;
          byPool.set(sn, {
            poolSn: sn,
            label: certDisplayName(row) + (styleNo ? ` · ${styleNo}` : ''),
            certificateTypeId: row.certificateTypeId,
            certificateTypeCode: row.certificateTypeCode,
            styleNo,
            styleMatched: Boolean(styleKey && styleNo.toLowerCase() === styleKey),
          });
        };
        styleList.forEach((row) => pushRow(row, true));
        typeList.forEach((row) => pushRow(row, false));
        const options = [...byPool.values()].sort((a, b) => {
          if (a.styleMatched !== b.styleMatched) return a.styleMatched ? -1 : 1;
          return String(a.label).localeCompare(String(b.label), 'zh');
        });
        setCertOptions(options);

        // 已有 seed.poolSn 或用户已选手动选择时不覆盖
        if (autoPickedRef.current) return;
        const matched = options.filter((item) => item.styleMatched);
        if (matched.length === 1) {
          setPoolSn(matched[0].poolSn);
          autoPickedRef.current = true;
          showToast(t('certForm.matchedOne', { name: matched[0].label }), 'success');
        } else if (matched.length > 1) {
          setPoolSn(matched[0].poolSn);
          autoPickedRef.current = true;
          showToast(t('certForm.matchedMany', { count: matched.length, name: matched[0].label }), 'info');
        }
      })
      .catch(() => {
        if (!cancelled) setCertOptions([]);
      })
      .finally(() => {
        if (!cancelled) setCertsLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, certificateTypeId, styleKey, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const onNeedFilePick = async (itemKey, item, file) => {
    if (!shopId || !file) return;
    const asImage = isImageNeedItem(item);
    setNeedUploads((prev) => ({
      ...prev,
      [itemKey]: { ...(prev[itemKey] || {}), uploading: true, fileName: file.name },
    }));
    try {
      const base64 = await fileToBase64(file);
      if (asImage) {
        const res = await temuPlatformAPI.uploadCompliancePhotoFile(shopId, {
          image: base64,
          clientId: `need-${itemKey}-${Date.now()}`,
        });
        const imageUrl = res?.result?.imageUrl || res?.imageUrl || res?.result?.url || res?.url || '';
        const imageMd5 = res?.result?.imageMd5 || res?.imageMd5 || res?.result?.md5 || res?.md5 || '';
        if (!imageUrl) throw new Error(t('certForm.imageNoUrl'));
        setNeedUploads((prev) => ({
          ...prev,
          [itemKey]: {
            fileName: file.name,
            imageUrl,
            imageMd5,
            fileUrl: imageUrl,
            uploading: false,
          },
        }));
        showToast(t('certForm.convertedImage', { name: file.name }), 'success');
      } else {
        const res = await temuPlatformAPI.uploadComplianceCertificateFile(shopId, { base64File: base64 });
        const fileUrl = res?.result?.url || res?.url || '';
        if (!fileUrl) throw new Error(t('certForm.fileNoUrl'));
        setNeedUploads((prev) => ({
          ...prev,
          [itemKey]: { fileName: file.name, fileUrl, uploading: false },
        }));
        showToast(t('certForm.convertedFile', { name: file.name }), 'success');
      }
    } catch (e) {
      setNeedUploads((prev) => ({
        ...prev,
        [itemKey]: { ...(prev[itemKey] || {}), uploading: false },
      }));
      showToast(e?.message || t('certForm.convertFailed'), 'error');
    }
  };

  const submit = async () => {
    if (!shopId) return;
    const skcNames = splitSkcNames(skcName);
    if (!skcNames.length) {
      showToast(t('certForm.missingSkc'), 'error');
      return;
    }
    if (!certificateTypeId) {
      showToast(t('certForm.pickType'), 'error');
      return;
    }
    const typeIdNum = Number(certificateTypeId);
    const pid = resolveProductId(seed, skcName);
    const uploadedFiles = Object.values(needUploads)
      .filter((u) => u?.fileUrl || u?.imageUrl)
      .map((u) => ({
        fileName: u.fileName || 'file',
        fileUrl: u.fileUrl || u.imageUrl,
        showCustomer: true,
      }));

    // 优先：按 needUploadItems 上传的文件直接走 uploadProductCert
    if (uploadedFiles.length > 0) {
      if (!(pid > 0)) {
        showToast(t('certForm.missingProduct'), 'error');
        return;
      }
      const requiredMissing = needItems.some((item, idx) => {
        if (!item?.required) return false;
        const key = `${Number(item?.itemType) || 0}-${idx}`;
        return !(needUploads[key]?.fileUrl || needUploads[key]?.imageUrl);
      });
      if (requiredMissing) {
        showToast(t('certForm.requiredPending'), 'warning');
        return;
      }
      setSaving(true);
      try {
        const res = await temuPlatformAPI.uploadProductCertificate(shopId, {
          productId: pid,
          productCatCertReqList: [{
            certType: typeIdNum,
            productCertFiles: uploadedFiles,
          }],
        });
        const ok = res?.success === true
          || Number(res?.errorCode) === 1000000
          || Number(res?.errorCode) === 0;
        if (!ok && res?.errorMsg) throw new Error(res.errorMsg);
        showToast(t('certForm.submitted'), 'success');
        onSaved?.();
      } catch (e) {
        showToast(e?.message || t('certForm.submitFailed'), 'error');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (!poolSn?.trim()) {
      showToast(needItems.length
        ? t('certForm.needUploadOrName')
        : t('certForm.pickName'), 'error');
      return;
    }
    const selectedCert = certOptions.find((item) => String(item.poolSn) === String(poolSn.trim())) || null;
    const typeCode = String(selectedCert?.certificateTypeCode || '').trim();
    setSaving(true);
    try {
      await temuPlatformAPI.bindGoodsCertificates(shopId, {
        poolSn: poolSn.trim(),
        skcNames,
        certType: Number.isFinite(typeIdNum) && typeIdNum > 0 ? typeIdNum : undefined,
        certificateTypeId: certificateTypeId || undefined,
        certificateTypeCode: typeCode || undefined,
        productId: pid > 0 ? pid : undefined,
      });
      showToast(t('certForm.boundOk', { no: poolSn.trim() }), 'success');
      onSaved?.();
    } catch (e) {
      showToast(e?.message || t('shop.bindFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-cert-modal-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-cert-modal-title">{t('certForm.titleBindUpload')}</h2>
          <button
            type="button"
            className="tpo-btn tpo-btn--ghost tpo-btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            {t('moduleShell.close')}
          </button>
        </div>

        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <p className="tpo-batch-modal__hint">
            {t('certForm.chainHint')}
            <Link className="tpo-hint-link" to={libraryPath}>{t('certForm.library')}</Link>
            。
            {styleNoLabel ? (
              <span className="tpo-batch-modal__hint-meta">{t('certForm.currentStyle', { no: styleNoLabel })}</span>
            ) : null}
          </p>

          <div className="tpo-cert-modal__grid">
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">SKC <RequiredMark /></span>
              <input
                className="tpo-input"
                type="text"
                value={skcName}
                disabled
                readOnly
                placeholder={t('certForm.skcPh')}
              />
            </label>

            <label className="tpo-field">
              <span className="tpo-field__label">{t('certLib.colType')} <RequiredMark /></span>
              <select
                className="tpo-select"
                value={certificateTypeId}
                disabled={saving}
                onChange={(e) => {
                  setCertificateTypeId(e.target.value);
                  setPoolSn('');
                  autoPickedRef.current = false;
                }}
              >
                <option value="">{t('shop.pleaseSelect')}</option>
                {typeOptions.map((item) => (
                  <option key={item.certificateTypeId} value={item.certificateTypeId}>
                    {item.certificateType || item.certificateTypeName || item.certificateTypeId}
                  </option>
                ))}
              </select>
            </label>

            <label className="tpo-field">
              <span className="tpo-field__label">{t('certForm.certName')} <RequiredMark /></span>
              <CertificateNameCombobox
                options={certOptions}
                value={poolSn}
                disabled={saving || !certificateTypeId}
                loading={certsLoading}
                placeholder={
                  certificateTypeId
                    ? (styleKey ? t('certForm.styleFirst') : t('certForm.filterPh'))
                    : t('certForm.pickTypeFirst')
                }
                onChange={(next) => {
                  autoPickedRef.current = Boolean(next);
                  setPoolSn(next);
                }}
              />
            </label>
          </div>

          {certificateTypeId && productId > 0 ? (
            <div className="tpo-cert-need-upload" style={{ marginTop: 16 }}>
              <h3 className="tpo-field__label" style={{ marginBottom: 8 }}>
                {t('certForm.pendingItems')}
              </h3>
              {needLoading ? (
                <p className="tpo-muted">{t('certForm.queryingNeed')}</p>
              ) : needItems.length === 0 ? (
                <p className="tpo-muted">{t('certForm.noNeedItems')}</p>
              ) : (
                <ul className="tpo-compliance-info-edit__list">
                  {needItems.map((item, idx) => {
                    const key = `${Number(item?.itemType) || 0}-${idx}`;
                    const name = String(item?.itemName || item?.name || t('certForm.uploadItem', { n: idx + 1 })).trim();
                    const required = Boolean(item?.required);
                    const asImage = isImageNeedItem(item);
                    const uploaded = needUploads[key];
                    const busy = Boolean(uploaded?.uploading);
                    return (
                      <li key={key} className="tpo-compliance-info-edit__row">
                        <div className="tpo-compliance-info-edit__meta">
                          <strong>{name}</strong>
                          {required ? <RequiredMark /> : null}
                          <span className="tpo-muted">
                            {asImage ? t('certForm.imageFlash') : t('certForm.fileUpload')}
                          </span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <label className="tpo-btn tpo-btn--secondary tpo-btn--sm" style={{ cursor: busy || saving ? 'not-allowed' : 'pointer' }}>
                            {busy ? t('certForm.converting') : (asImage ? t('certForm.pickImage') : t('certForm.pickFile'))}
                            <input
                              type="file"
                              accept={asImage ? 'image/*' : '.pdf,.png,.jpg,.jpeg,.doc,.docx,.zip'}
                              hidden
                              disabled={busy || saving}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (file) onNeedFilePick(key, item, file);
                              }}
                            />
                          </label>
                          {uploaded?.fileUrl || uploaded?.imageUrl ? (
                            <span className="tpo-muted" title={uploaded.fileUrl || uploaded.imageUrl}>
                              {t('certForm.convertedMark', { name: uploaded.fileName || t('certForm.fileWord') })}
                            </span>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        <div className="tpo-batch-modal__foot">
          <button
            type="button"
            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
            onClick={onClose}
            disabled={saving}
          >
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={submit}
            disabled={saving || !shopId || !skcName}
          >
            {saving
              ? t('returnList.submitting')
              : (Object.values(needUploads).some((u) => u?.fileUrl || u?.imageUrl) ? t('certForm.submitUpload') : t('certForm.confirmBind'))}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
